import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { License, Transaction, AccessLog } from '../types/database';
import {
    UsersIcon,
    CurrencyEuroIcon,
    KeyIcon,
    ChartBarIcon,
    CalendarIcon,
    EnvelopeIcon,
    DocumentTextIcon,
    MagnifyingGlassIcon,
    CogIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import LicenseTypeEditor from '../components/admin/LicenseTypeEditor';

interface AdminStats {
    totalUsers: number;
    totalLicenses: number;
    totalRevenue: number;
    activeUsers: number;
}

interface UserWithLicense {
    id: string;
    email: string;
    full_name?: string;
    created_at: string;
    is_active?: boolean;
    licenses: License[];
}

export default function AdminPage() {
    const [stats, setStats] = useState<AdminStats>({
        totalUsers: 0,
        totalLicenses: 0,
        totalRevenue: 0,
        activeUsers: 0
    });
    const [users, setUsers] = useState<UserWithLicense[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'stats' | 'users' | 'transactions' | 'logs' | 'license-types'>('stats');
    
    // États pour les recherches
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
    const [logSearchTerm, setLogSearchTerm] = useState('');

    useEffect(() => {
        loadAdminData();
    }, []);

    const loadAdminData = async () => {
        try {
            setLoading(true);

            // Charger les statistiques générales
            const [usersResponse, licensesResponse, transactionsResponse, accessLogsResponse] = await Promise.all([
                supabase.from('users').select('*'),
                supabase.from('licenses').select('*, license_types(*)'),
                supabase.from('transactions').select('*'),
                supabase.from('access_logs').select('*').order('created_at', { ascending: false }).limit(100)
            ]);

            if (usersResponse.error) throw usersResponse.error;
            if (licensesResponse.error) throw licensesResponse.error;
            if (transactionsResponse.error) throw transactionsResponse.error;
            if (accessLogsResponse.error) throw accessLogsResponse.error;

            const allUsers = usersResponse.data || [];
            const allLicenses = licensesResponse.data || [];
            const allTransactions = transactionsResponse.data || [];
            const allAccessLogs = accessLogsResponse.data || [];

            // Calculer les statistiques - les montants sont déjà en euros
            const totalRevenue = allTransactions
                .filter(t => t.status === 'completed')
                .reduce((sum, t) => sum + t.amount, 0);

            const activeUsers = allUsers.filter(u => u.is_active !== false).length;

            setStats({
                totalUsers: allUsers.length,
                totalLicenses: allLicenses.length,
                totalRevenue,
                activeUsers
            });

            // Charger les utilisateurs avec leurs licences
            const usersWithLicenses: UserWithLicense[] = allUsers.map(user => ({
                ...user,
                licenses: allLicenses.filter(license => license.user_id === user.id)
            }));

            setUsers(usersWithLicenses);
            setTransactions(allTransactions);
            setAccessLogs(allAccessLogs);

        } catch (error) {
            console.error('Erreur lors du chargement des données admin:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fonctions de filtrage
    const filteredUsers = users.filter(user => {
        if (!userSearchTerm) return true;
        const searchLower = userSearchTerm.toLowerCase();
        return (
            user.email.toLowerCase().includes(searchLower) ||
            (user.full_name && user.full_name.toLowerCase().includes(searchLower))
        );
    });

    const filteredTransactions = transactions.filter(transaction => {
        if (!transactionSearchTerm) return true;
        const searchLower = transactionSearchTerm.toLowerCase();
        const user = users.find(u => u.id === transaction.user_id);
        return (
            user?.email.toLowerCase().includes(searchLower) ||
            (user?.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
            transaction.payment_reference?.toLowerCase().includes(searchLower)
        );
    });

    const filteredAccessLogs = accessLogs.filter(log => {
        if (!logSearchTerm) return true;
        const searchLower = logSearchTerm.toLowerCase();
        const user = users.find(u => u.id === log.user_id);
        return (
            user?.email.toLowerCase().includes(searchLower) ||
            (user?.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
            log.action.toLowerCase().includes(searchLower) ||
            log.ip_address?.toLowerCase().includes(searchLower)
        );
    });

    // Composant de champ de recherche
    const SearchInput = ({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) => (
        <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-3 mb-2 text-center">
                    Administration MP3 Random Selector
                </h1>
                <p className="text-gray-600 dark:text-gray-400 m-2 text-center">
                    Tableau de bord administrateur
                </p>
            </div>

            {/* Navigation des onglets */}
            <div className="mb-8">
                <nav className="flex space-x-8" aria-label="Tabs">
                    {[
                        { id: 'stats', name: 'Statistiques', icon: ChartBarIcon },
                        { id: 'users', name: 'Utilisateurs', icon: UsersIcon },
                        { id: 'license-types', name: 'Types de licences', icon: CogIcon },
                        { id: 'transactions', name: 'Transactions', icon: CurrencyEuroIcon },
                        { id: 'logs', name: 'Logs d\'accès', icon: DocumentTextIcon }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as 'stats' | 'users' | 'license-types' | 'transactions' | 'logs')}
                            className={`${activeTab === tab.id
                                    ? 'border-purple-500 text-purple-500 dark:text-purple-400'
                                    : 'border-transparent text-white hover:text-purple-400 hover:border-gray-300'
                                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                        >
                            <tab.icon className="h-5 w-5" />
                            <span>{tab.name}</span>
                        </button>
                    ))}
                </nav>
            </div>

            {/* Contenu des onglets */}
            {activeTab === 'stats' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <UsersIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Total Utilisateurs
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                        {stats.totalUsers}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <UsersIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Utilisateurs Actifs
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                        {stats.activeUsers}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <KeyIcon className="h-8 w-8 text-purple-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Licences Vendues
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                        {stats.totalLicenses}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CurrencyEuroIcon className="h-8 w-8 text-yellow-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Revenus Total
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                        {stats.totalRevenue.toFixed(2)} €
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'users' && (
                <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                            Liste des Utilisateurs
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                            Informations détaillées sur tous les utilisateurs inscrits
                        </p>
                    </div>
                    <div className="px-4 sm:px-6">
                        <SearchInput
                            value={userSearchTerm}
                            onChange={setUserSearchTerm}
                            placeholder="Rechercher par nom ou email..."
                        />
                    </div>
                    <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredUsers.map((user) => (
                            <li key={user.id} className="px-4 py-4 sm:px-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                                                <EnvelopeIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <div className="flex items-center">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {user.full_name || 'Nom non renseigné'}
                                                </p>
                                                {user.is_active === false && (
                                                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                        Désactivé
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {user.email}
                                            </p>
                                            <div className="flex items-center mt-1">
                                                <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    Inscrit le {format(new Date(user.created_at), 'dd MMMM yyyy', { locale: fr })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <div className="flex items-center">
                                            <KeyIcon className="h-4 w-4 text-gray-400 mr-1" />
                                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                                {user.licenses.length} licence(s)
                                            </span>
                                        </div>
                                        {user.licenses.length > 0 && (
                                            <div className="mt-1">
                                                {user.licenses.map((license) => (
                                                    <div key={license.id} className="text-xs text-gray-500 dark:text-gray-400">
                                                        Licence : {license.license_key}
                                                        <span className={`ml-2 px-2 py-1 rounded text-xs ${license.status === 'active'
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                                            }`}>
                                                            {license.status}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                        {filteredUsers.length === 0 && (
                            <div className="text-center py-8">
                                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {userSearchTerm ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {userSearchTerm 
                                        ? 'Aucun utilisateur ne correspond à votre recherche.'
                                        : 'Aucun utilisateur inscrit pour le moment.'
                                    }
                                </p>
                            </div>
                        )}
                    </ul>
                </div>
            )}

            {activeTab === 'license-types' && (
                <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
                    <LicenseTypeEditor onRefresh={loadAdminData} />
                </div>
            )}

            {activeTab === 'transactions' && (
                <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                            Historique des Transactions
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                            Liste de toutes les transactions effectuées
                        </p>
                    </div>
                    <div className="px-4 sm:px-6">
                        <SearchInput
                            value={transactionSearchTerm}
                            onChange={setTransactionSearchTerm}
                            placeholder="Rechercher par utilisateur ou référence..."
                        />
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Utilisateur
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Montant
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Référence
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredTransactions.map((transaction) => {
                                    const user = users.find(u => u.id === transaction.user_id);

                                    return (
                                        <tr key={transaction.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {format(new Date(transaction.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user?.email || 'Utilisateur inconnu'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {transaction.amount.toFixed(2)} {transaction.currency.toUpperCase()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${transaction.status === 'completed'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : transaction.status === 'pending'
                                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                            : transaction.status === 'failed'
                                                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                                    }`}>
                                                    {transaction.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {transaction.payment_reference || 'N/A'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredTransactions.length === 0 && (
                            <div className="text-center py-8">
                                <CurrencyEuroIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {transactionSearchTerm ? 'Aucune transaction trouvée' : 'Aucune transaction'}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {transactionSearchTerm 
                                        ? 'Aucune transaction ne correspond à votre recherche.'
                                        : 'Aucune transaction effectuée pour le moment.'
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'logs' && (
                <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                            Logs d'accès à l'application
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                            Historique des accès et actions effectuées dans l'application (100 derniers logs)
                        </p>
                    </div>
                    <div className="px-4 sm:px-6">
                        <SearchInput
                            value={logSearchTerm}
                            onChange={setLogSearchTerm}
                            placeholder="Rechercher par utilisateur, action ou IP..."
                        />
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Date & Heure
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Utilisateur
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Action
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        IP
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Erreur
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredAccessLogs.map((log) => {
                                    const user = users.find(u => u.id === log.user_id);

                                    return (
                                        <tr key={log.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {user ? (
                                                    <div>
                                                        <div className="font-medium">{user.full_name || 'Nom non renseigné'}</div>
                                                        <div className="text-gray-500 dark:text-gray-400 text-xs">{user.email}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-500 dark:text-gray-400">Utilisateur inconnu</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                <div>
                                                    <div className="font-medium">{log.action}</div>
                                                    {log.license_id && (
                                                        <div className="text-gray-500 dark:text-gray-400 text-xs">
                                                            Licence: {log.license_id.substring(0, 8)}...
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {log.ip_address || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    log.success
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                }`}>
                                                    {log.success ? 'Succès' : 'Échec'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                                {log.error_message || '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredAccessLogs.length === 0 && (
                            <div className="text-center py-8">
                                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {logSearchTerm ? 'Aucun résultat trouvé' : 'Aucun log trouvé'}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {logSearchTerm 
                                        ? 'Aucun log ne correspond à votre recherche.'
                                        : 'Aucun log d\'accès n\'a été trouvé pour le moment.'
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
